"use client";

import React from "react";
import { ArrowRight, BookOpen, Clock, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function PostsGridConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => <Card key={i} className="border border-border/80 bg-card overflow-hidden">
            <div className="mf-card-media mf-ratio-card bg-muted relative w-full overflow-hidden" />
            <CardHeader className="space-y-3 pt-5">
              <div className="h-4 w-24 bg-muted rounded-sm" />
              <div className="h-6 w-full bg-muted rounded-sm" />
              <div className="h-6 w-3/4 bg-muted rounded-sm" />
            </CardHeader>
            <CardContent className="space-y-2 pb-6">
              <div className="h-4 w-full bg-muted rounded-sm" />
              <div className="h-4 w-5/6 bg-muted rounded-sm" />
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-lg border border-border bg-card p-8 text-center max-w-xl mx-auto">
        <FileText className="mx-auto size-10 text-muted-foreground mb-3" aria-hidden="true" />
        <p className="text-sm text-muted-foreground mb-4">
          Unable to load technical articles at this time.
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Retry loading
        </Button>
      </div>;
  }

  // Fallback items if post query yields no items
  const displayPosts = posts.length > 0 ? posts : [{
    id: "fallback-1",
    title: "ASTM E2016 wire cloth specification standards explained",
    excerpt: "A comprehensive analysis of standard industrial woven wire cloth tolerances, aperture measurement, and quality compliance criteria.",
    link: "/AstmE2016ExplainedPage",
    date: "2025-02-15",
    categories: [{
      id: 1,
      name: "Standards & Specs",
      slug: "standards",
      taxonomy: "category"
    }],
    featuredImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/astm_standards_mesh.png"
  }, {
    id: "fallback-2",
    title: "Optimizing polymer melt filtration with sintered multilayer mesh",
    excerpt: "How multi-layered sintered wire mesh structures enhance pressure resistance, shear stability, and gel capture capabilities in high-temperature extrusions.",
    link: "/PolymerFiltrationPage",
    date: "2025-02-10",
    categories: [{
      id: 2,
      name: "Polymer Filtration",
      slug: "polymer",
      taxonomy: "category"
    }],
    featuredImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/polymer_sintered_mesh.png"
  }, {
    id: "fallback-3",
    title: "Stainless steel grade selection: 304, 316, and 316L for corrosive fluids",
    excerpt: "Key corrosion resistance matrices comparing austenitic and alloy grades across chemical processing and liquid filtration environments.",
    link: "/CorrosionResistanceMatricesPage",
    date: "2025-01-28",
    categories: [{
      id: 3,
      name: "Material Verification",
      slug: "materials",
      taxonomy: "category"
    }],
    featuredImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/stainless_steel_matrix.webp"
  }];
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {displayPosts.map((post, i) => {
      const categoryName = post.categories?.[0]?.name || "Technical Resource";
      const postLink = post.link || "/TechnicalResourcesPage";
      return <Card key={post.id} data-index={i} className="flex flex-col h-full border border-border bg-card hover:border-primary/40 transition-colors duration-200 overflow-hidden mf-hover-lift">
            {/* Image / Header Frame */}
            <div className="mf-card-media mf-ratio-card relative w-full overflow-hidden">
              {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-muted p-6 flex flex-col justify-between relative overflow-hidden">
                  <div className="flex items-center justify-between z-10">
                    <Badge variant="outline" className="bg-card text-foreground border border-border mf-caption">
                      {categoryName}
                    </Badge>
                    <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-card text-primary">
                      <BookOpen className="size-5" aria-hidden="true" />
                    </div>
                  </div>
                  <div className="mf-caption font-mono text-muted-foreground z-10 flex items-center gap-1.5">
                    <Clock className="size-3.5" aria-hidden="true" />
                    <span>Industrial Filtration Insight</span>
                  </div>
                </div>}
              {post.featuredImage && <div className="absolute top-3 left-3 z-10">
                  <Badge variant="outline" className="bg-card text-foreground border border-border mf-caption font-medium">
                    {categoryName}
                  </Badge>
                </div>}
            </div>

            {/* Card Body */}
            <CardHeader className="pt-5 pb-2 px-6 space-y-2">
              <div className="mf-caption font-mono text-muted-foreground flex items-center gap-2">
                <span>{post.date ? new Date(post.date).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric"
              }) : "Technical Publication"}</span>
                <span>•</span>
                <span>MutualFilter Technical Engineering</span>
              </div>
              <CardTitle className="text-lg font-semibold tracking-tight leading-snug hover:text-primary transition-colors">
                <Link to={postLink} className="focus:outline-none hover:text-primary">
                  {post.title}
                </Link>
              </CardTitle>
            </CardHeader>

            <CardContent className="px-6 pb-6 pt-0 flex-1 flex flex-col justify-between space-y-4">
              <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed">
                {post.excerpt ? post.excerpt.replace(/<[^>]*>?/gm, '') : "Read detailed technical guidance, engineering specifications, and selection matrices for industrial filter wire mesh applications."}
              </p>

              <div className="pt-2 border-t border-border/40 flex items-center text-sm font-medium text-primary">
                <Link to={postLink} className="inline-flex items-center gap-1.5 hover:underline text-primary font-medium">
                  <span>Read full guide</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </div>
            </CardContent>
          </Card>;
    })}
    </div>;
}
export default function RelatedArticles() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="relatedarticles" data-nav="light" className="relative bg-background text-foreground mf-section border-t border-border overflow-hidden">
      <div className="mf-container relative z-10 space-y-10">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-border/80 pb-8">
          <div className="max-w-2xl space-y-3">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md border border-border bg-card mf-caption font-mono font-medium text-foreground">
              <BookOpen className="size-3.5 text-primary" aria-hidden="true" />
              <span>Technical Knowledge Center</span>
            </div>
            <h2 className="mf-h2 text-foreground">
              Related technical guides and filtration insights
            </h2>
            <p className="mf-lead text-muted-foreground">
              In-depth engineering documentation, wire mesh selection standards, and industrial separation technology resources.
            </p>
          </div>

          <div className="shrink-0">
            <Button asChild variant="outline" className="border-border text-foreground">
              <Link to="/TechnicalResourcesPage" className="inline-flex items-center gap-2">
                <FileText className="size-4" aria-hidden="true" />
                <span>View all technical articles</span>
              </Link>
            </Button>
          </div>
        </div>

        {/* Dynamic Articles Grid */}
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <PostsGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}